/**
 * Utility functions for determining agent mode.
 */

export const AgentMode = {
  ANALYZE: 'analyze',
  PLAN: 'plan',
  EXECUTE: 'execute',
  FAST: 'fast',
} as const;

export type AgentMode = (typeof AgentMode)[keyof typeof AgentMode];

/**
 * Extract planId from extraMetadata and determine agent mode.
 * Execute mode is set explicitly when execute button is pressed (planId present).
 * Otherwise uses mode from agentConfig or defaults to analyze.
 *
 * @param agentConfig - Agent configuration that may contain mode
 * @param extraMetadata - Optional extra metadata that may contain planId
 * @returns Calculated agent mode
 */
export function extractPlanIdAndMode(
  agentConfig?: { mode?: AgentMode } | null,
  extraMetadata?: Record<string, unknown> | null
): AgentMode {
  const planId =
    (extraMetadata?.planId as string | undefined) || undefined;

  // Execute mode: when execute button is pressed (planId is present)
  if (planId) {
    return AgentMode.EXECUTE;
  }

  // Use mode from agentConfig if set
  if (agentConfig?.mode) {
    return agentConfig.mode;
  }

  // Default to analyze mode
  return AgentMode.ANALYZE;
}

/**
 * Toggle plan mode - switches between 'plan' and 'analyze'.
 *
 * @param currentMode - Current agent mode
 * @returns Toggled mode ('plan' if current is 'analyze', 'analyze' if current is 'plan')
 */
export function togglePlanMode(currentMode?: AgentMode): AgentMode {
  return currentMode === AgentMode.PLAN
    ? AgentMode.ANALYZE
    : AgentMode.PLAN;
}

/**
 * Toggle fast mode - switches between 'fast' and 'analyze'.
 *
 * @param currentMode - Current agent mode
 * @returns Toggled mode ('fast' if current is 'analyze', 'analyze' if current is 'fast')
 */
export function toggleFastMode(currentMode?: AgentMode): AgentMode {
  return currentMode === AgentMode.FAST
    ? AgentMode.ANALYZE
    : AgentMode.FAST;
}
